import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';
import { resellerAPI, applicationsAPI } from '../services/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Trophy, Lock, Crown, Clock } from 'lucide-react';

const ResellerRankingPublic = () => {
  const [rankingType, setRankingType] = useState('weekly'); // 'weekly' ou 'general'
  const [typeFilter, setTypeFilter] = useState('AGENTE'); // 'HOST' ou 'AGENTE'
  const [weeklyRanking, setWeeklyRanking] = useState([]);
  const [generalRanking, setGeneralRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    loadRankings();
  }, []);

  // Cronômetro da semana
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const endOfWeek = new Date(now);
      const daysUntilSunday = 7 - now.getDay();
      endOfWeek.setDate(now.getDate() + (daysUntilSunday === 7 ? 0 : daysUntilSunday));
      endOfWeek.setHours(23, 59, 59, 999);
      
      const diff = endOfWeek.getTime() - now.getTime();
      
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setTimeRemaining({ days, hours, minutes, seconds });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadRankings = async () => {
    try {
      const [weeklyRes, generalRes] = await Promise.all([
        resellerAPI.getRankingWeekly(),
        resellerAPI.getRankingAdmin()
      ]);
      setWeeklyRanking(weeklyRes.data);
      setGeneralRanking(generalRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsActive(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50 flex items-center justify-center">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Lock className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Ranking Não Disponível</h2>
            <p className="text-gray-600">
              O ranking de afiliados não está ativo no momento.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentRanking = rankingType === 'weekly' ? weeklyRanking : generalRanking;
  const filteredRanking = currentRanking.filter(r => r.type === typeFilter);

  const renderRankingCard = (reseller) => (
    <Card 
      key={reseller.id}
      className={`transition-all hover:shadow-lg ${
        reseller.position === 1 ? 'border-yellow-400 border-2' :
        reseller.position === 2 ? 'border-gray-400 border-2' :
        reseller.position === 3 ? 'border-orange-400 border-2' : ''
      }`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Posição */}
          <div className="text-center min-w-[40px] sm:min-w-[60px]">
            <div className="text-2xl sm:text-3xl font-bold">
              {reseller.position === 1 && '🥇'}
              {reseller.position === 2 && '🥈'}
              {reseller.position === 3 && '🥉'}
              {reseller.position > 3 && `${reseller.position}º`}
            </div>
          </div>

          {/* Avatar */}
          {reseller.kako_avatar && (
            <img
              src={reseller.kako_avatar}
              alt={reseller.nickname}
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-white shadow-lg"
            />
          )}

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 truncate">
              {reseller.nickname || 'Afiliado'}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 truncate">ID: {reseller.kako_id}</p>
            <Badge variant={reseller.type === 'HOST' ? 'default' : 'secondary'} className="text-xs mt-1">
              {reseller.type}
            </Badge>
          </div>

          {/* Vendas */}
          <div className="text-right">
            <div className="text-lg sm:text-3xl font-bold text-purple-600">
              {reseller.total_sales_coins.toLocaleString()} 🪙
            </div>
            <p className="text-xs text-gray-600 hidden sm:block">
              {rankingType === 'weekly' ? 'Vendas da Semana' : 'Total de Vendas'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-blue-50 p-2 sm:p-4">
      <div className="max-w-4xl mx-auto py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold mb-2">
            <span className="text-orange-500">🦊 FOX</span>{' '}
            <span className="text-purple-600">SELLER</span>
          </h1>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2">
            <Trophy className="inline h-6 w-6 sm:h-8 sm:w-8 text-yellow-500 mr-2" />
            Ranking de Afiliados
          </h2>
          <p className="text-sm sm:text-base text-gray-600">Top revendedores por vendas</p>
        </div>

        {/* Tabs de Ranking */}
        <Tabs value={rankingType} onValueChange={(v) => { setRankingType(v); setTypeFilter('AGENTE'); }} className="mb-4">
          <TabsList className="grid w-full grid-cols-2 bg-gray-100">
            <TabsTrigger value="weekly" className="flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:bg-purple-100 data-[state=inactive]:text-purple-600">
              <Clock className="h-4 w-4" />
              Ranking Semanal
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=inactive]:bg-orange-100 data-[state=inactive]:text-orange-600">
              <Crown className="h-4 w-4" />
              Ranking Geral
            </TabsTrigger>
          </TabsList>

          {/* Ranking Semanal */}
          <TabsContent value="weekly" className="space-y-4 mt-4">
            {/* Filtro HOST/AGENTE */}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setTypeFilter('AGENTE')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  typeFilter === 'AGENTE'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-purple-100 text-purple-600'
                }`}
              >
                💼 AGENTES
              </button>
              <button
                onClick={() => setTypeFilter('HOST')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  typeFilter === 'HOST'
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-orange-100 text-orange-600'
                }`}
              >
                🎭 HOSTS
              </button>
            </div>

            {/* Cronômetro ABAIXO das sub-abas */}
            <Card className="bg-gradient-to-r from-purple-500 to-orange-500 text-white">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="h-5 w-5" />
                  <h3 className="text-lg font-bold">Tempo Restante da Semana</h3>
                </div>
                <div className="flex justify-center gap-4 text-2xl font-bold">
                  <div>
                    <div>{timeRemaining.days}</div>
                    <div className="text-xs font-normal">Dias</div>
                  </div>
                  <div>:</div>
                  <div>
                    <div>{String(timeRemaining.hours).padStart(2, '0')}</div>
                    <div className="text-xs font-normal">Horas</div>
                  </div>
                  <div>:</div>
                  <div>
                    <div>{String(timeRemaining.minutes).padStart(2, '0')}</div>
                    <div className="text-xs font-normal">Min</div>
                  </div>
                  <div>:</div>
                  <div>
                    <div>{String(timeRemaining.seconds).padStart(2, '0')}</div>
                    <div className="text-xs font-normal">Seg</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Ranking Semanal */}
            <div className="space-y-2 sm:space-y-3">
              {filteredRanking.map(renderRankingCard)}
            </div>

            {filteredRanking.length === 0 && (
              <Card>
                <CardContent className="text-center py-12 text-gray-500">
                  <p>Nenhum {typeFilter === 'HOST' ? 'host' : 'agente'} no ranking ainda</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Ranking Geral */}
          <TabsContent value="general" className="space-y-4 mt-4">
            {/* Filtro HOST/AGENTE */}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setTypeFilter('AGENTE')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  typeFilter === 'AGENTE'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-purple-100 text-purple-600'
                }`}
              >
                💼 AGENTES
              </button>
              <button
                onClick={() => setTypeFilter('HOST')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  typeFilter === 'HOST'
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-orange-100 text-orange-600'
                }`}
              >
                🎭 HOSTS
              </button>
            </div>

            {/* Lista de Ranking Geral */}
            <div className="space-y-2 sm:space-y-3">
              {filteredRanking.map(renderRankingCard)}
            </div>

            {filteredRanking.length === 0 && (
              <Card>
                <CardContent className="text-center py-12 text-gray-500">
                  <p>Nenhum {typeFilter === 'HOST' ? 'host' : 'agente'} no ranking ainda</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-8 text-gray-600">
          <p className="text-xs sm:text-sm">🦊 FOX SELLER - Sistema de Vendas KAKO LIVE</p>
        </div>
      </div>
    </div>
  );
};

export default ResellerRankingPublic;
